import React, { useRef, useCallback, useState } from 'react';
import { Box, Typography, Grid, Button, Stack, IconButton } from "@mui/material";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { DynamicFieldAdvanced } from '../../../../../../components/common/input/DynamicFieldAdvanced';
import { useSelector, useDispatch } from 'react-redux';
import { setEducationList, setStepCompletion } from '../../../../../../store';
import { saveEmployeeEducation } from "../../../../../../api/employeeApi";
import stateList from "../../../../../../utils/states.json";
import { useNotification } from "../../../../../../hooks/useNotification";

const FileList = ({ files, onRemove }) => {
  if (!files || files.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
      {files.map((file, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: '20px',
            bgcolor: 'rgba(16, 185, 129, 0.06)',
            border: '1px solid rgba(16, 185, 129, 0.15)',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.02)',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(16, 185, 129, 0.1)',
            }
          }}
        >
          {file.url ? (
            <Typography
              variant="caption"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const response = await fetch(`/api/onboarding/proxy-download?url=${encodeURIComponent(file.url)}`);
                  if (!response.ok) throw new Error('Proxy failed');
                  const blob = await response.blob();
                  const link = document.createElement('a');
                  link.href = window.URL.createObjectURL(blob);
                  link.download = file.name || file.url.split('/').pop().split('?')[0];
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } catch (err) {
                  window.open(file.url, '_blank');
                }
              }}
              sx={{
                color: "#059669",
                cursor: "pointer",
                textDecoration: "none",
                fontWeight: 700,
                fontSize: '0.75rem',
                "&:hover": { textDecoration: "underline" }
              }}
            >
              {file.name}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: '#065F46', fontWeight: 700, fontSize: '0.75rem' }}>{file.name}</Typography>
          )}
          <IconButton size="small" onClick={() => onRemove(idx)} sx={{ p: 0.25, ml: 0.5, color: '#059669', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)' } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
    </Stack>
  );
};


// const EducationItem = React.memo(({ edu, idx, errors, touched, onFieldChange, onFieldBlur, onRemove, canRemove }) => {
//   const handleChange = useCallback((name, value) => {
//     onFieldChange(idx, name, value);
//   }, [idx, onFieldChange]);

//   const handleBlur = useCallback((name) => {
//     onFieldBlur(idx, name);
//   }, [idx, onFieldBlur]);

//   const handleRemoveClick = useCallback(() => {
//     onRemove(idx);




const EducationalDetails = ({ onNext, onBack, employeeId }) => {
  const dispatch = useDispatch();
  const { showNotification, Notification } = useNotification();
  const educationList = useSelector((state) => state.basicDetails.educationList) || [];
  const fileInputRefs = useRef([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handler for file upload
  const handleFileUpload = useCallback((idx, event) => {
    const files = Array.from(event.target.files);
    const updated = educationList.map((x, i) =>
      i === idx ? { ...x, attachments: [...(x.attachments || []), ...files] } : x
    );
    dispatch(setEducationList(updated));
  }, [educationList, dispatch]);

  // Handler for removing a file
  const handleRemoveAttachment = useCallback((eduIdx, fileIdx) => {
    const updated = educationList.map((x, i) =>
      i === eduIdx
        ? { ...x, attachments: (x.attachments || []).filter((_, j) => j !== fileIdx) }
        : x
    );
    dispatch(setEducationList(updated));
  }, [educationList, dispatch]);

  // Handler for field change
  const handleFieldChange = useCallback((idx, name, value) => {
    const updated = educationList.map((x, i) =>
      i === idx ? { ...x, [name]: value } : x
    );
    dispatch(setEducationList(updated));

    // Clear error for this field
    const errorKey = `${name}_${idx}`;
    setErrors((prev) => {
      if (prev[errorKey]) {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      }
      return prev;
    });
  }, [educationList, dispatch]);

  const validate = () => {
    const newErrors = {};
    educationList.forEach((edu, idx) => {
      if (!edu.qualificationLevel?.trim()) {
        newErrors[`qualificationLevel_${idx}`] = 'Qualification level is required';
      }
      if (!edu.degree?.trim()) {
        newErrors[`degree_${idx}`] = 'Degree is required';
      }
      if (!edu.yearOfJoining) {
        newErrors[`yearOfJoining_${idx}`] = 'Year of joining is required';
      }
      if (!edu.yearOfCompletion) {
        newErrors[`yearOfCompletion_${idx}`] = 'Year of completion is required';
      } else if (edu.yearOfJoining && new Date(edu.yearOfCompletion) < new Date(edu.yearOfJoining)) {
        newErrors[`yearOfCompletion_${idx}`] = 'Completion year cannot be before joining year';
      }
      if (!edu.cgpa?.trim()) {
        newErrors[`cgpa_${idx}`] = 'CGPA/Percentage is required';
      }
      if (!edu.collegeName?.trim()) {
        newErrors[`collegeName_${idx}`] = 'College name is required';
      }
      if (!edu.university?.trim()) {
        newErrors[`university_${idx}`] = 'University is required';
      }
      if (!edu.state?.trim()) {
        newErrors[`state_${idx}`] = 'State is required';
      }
      if (!edu.attachments || edu.attachments.length === 0) {
        newErrors[`attachments_${idx}`] = 'Attachments are required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Mark all fields as touched
    const touchedFields = {};
    educationList.forEach((edu, idx) => {
      touchedFields[`qualificationLevel_${idx}`] = true;
      touchedFields[`degree_${idx}`] = true;
      touchedFields[`yearOfJoining_${idx}`] = true;
      touchedFields[`yearOfCompletion_${idx}`] = true;
      touchedFields[`cgpa_${idx}`] = true;
      touchedFields[`collegeName_${idx}`] = true;
      touchedFields[`university_${idx}`] = true;
      touchedFields[`state_${idx}`] = true;
      touchedFields[`attachments_${idx}`] = true;
    });
    setTouched(touchedFields);

    const isVal = validate();
    if (!isVal) {
      showNotification("Please fill in all required fields and upload mandatory attachments.", "error");
      return;
    }

    if (onNext) {
      if (!employeeId) {
        setErrors({ ...errors, submit: "Employee ID is missing" });
        return;
      }

      setIsSubmitting(true);
      try {
        const payload = new FormData();

        // Map data keeping field names aligned with backend expectations
        const mappedData = educationList.map(edu => ({
          qualificationLevel: edu.qualificationLevel,
          degree: edu.degree,
          yearOfJoining: edu.yearOfJoining,
          yearOfCompletion: edu.yearOfCompletion,
          collegeName: edu.collegeName,
          affiliatedUniversity: edu.university,
          cgpaPercentage: edu.cgpa,
          state: edu.state,
          documentPath: edu.attachments?.find(a => a.isExisting)?.url || "",
        }));

        payload.append('education', JSON.stringify(mappedData));

        // Attach files for each education entry
        educationList.forEach((edu, idx) => {
          if (edu.attachments) {
            edu.attachments.forEach(file => {
              if (file instanceof File) {
                payload.append(`educationDocument_${idx}`, file);
              }
            });
          }
        });

        await saveEmployeeEducation(employeeId, payload);

        dispatch(setStepCompletion({ step: 'education', isCompleted: true }));

        // Mark newly uploaded files as existing so they don't get re-uploaded
        const updatedList = educationList.map(edu => {
          if (edu.attachments) {
            const updatedAttachments = edu.attachments.map(file => {
              if (file instanceof File) {
                return {
                  name: file.name,
                  url: URL.createObjectURL(file),
                  isExisting: true
                };
              }
              return file;
            });
            return { ...edu, attachments: updatedAttachments };
          }
          return edu;
        });

        dispatch(setEducationList(updatedList));
        setErrors({});
        setTouched({});

        if (employeeId) {
          showNotification("Education details updated successfully", "success");
        }
        if (onNext) {
          onNext();
        }
      } catch (error) {
        console.error("Error saving education details:", error);
        showNotification(error.message || "Failed to update education details", "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSkip = () => {
    if (onNext) {
      onNext();
    }
  };

  // Handler for removing an education entry
  const handleRemoveEducation = useCallback((idx) => {
    if (educationList.length > 1) {
      const updated = educationList.filter((_, i) => i !== idx);
      dispatch(setEducationList(updated));
    }
  }, [educationList, dispatch]);

  // Handler for adding education entry
  const handleAddEducation = useCallback(() => {
    const updated = [
      ...educationList,
      {
        qualificationLevel: "",
        degree: "",
        yearOfJoining: "",
        yearOfCompletion: "",
        collegeName: "",
        university: "",
        cgpa: "",
        state: "",
        attachments: [],
      },
    ];
    dispatch(setEducationList(updated));
  }, [educationList, dispatch]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: 'wrap',
          gap: 2,
          mb: 3.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: '#F5EEFC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <SchoolOutlinedIcon sx={{ color: '#7522D1', fontSize: 18 }} />
          </Box>
          Educational Qualifications
        </Typography>
        <Button
          variant="outlined"
          onClick={handleAddEducation}
        >
          + Add Education
        </Button>
      </Box>

      {educationList.map((edu, idx) => (
        <Box key={`edu-${idx}`} sx={{ mb: 4, border: '1px solid rgba(117,34,209,0.08)', borderRadius: '12px', p: 3, position: 'relative', bgcolor: '#FAF9FB' }}>
          {/* Remove button for all except the first entry */}
          {educationList.length > 1 && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1,
                borderColor: 'rgba(239, 68, 68, 0.4)',
                color: '#EF4444',
                '&:hover': {
                  borderColor: '#EF4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.04)',
                }
              }}
              onClick={() => handleRemoveEducation(idx)}
            >
              Remove
            </Button>
          )}
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="select"
                label="Qualification Level"
                name="qualificationLevel"
                value={edu.qualificationLevel}
                onChange={(name, value) => handleFieldChange(idx, name, value)}
                required
                error={touched[`qualificationLevel_${idx}`] && !!errors[`qualificationLevel_${idx}`]}
                errorText={touched[`qualificationLevel_${idx}`] ? errors[`qualificationLevel_${idx}`] : ""}
                options={[
                  { value: "10th", label: "10th" },
                  { value: "12th", label: "12th/Diploma" },
                  { value: "Graduate", label: "Graduate" },
                  { value: "Post Graduate", label: "Post Graduate" },
                ]}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="text"
                label="Degree"
                name="degree"
                value={edu.degree}
                onChange={(name, value) => handleFieldChange(idx, name, value)}
                required
                error={touched[`degree_${idx}`] && !!errors[`degree_${idx}`]}
                errorText={touched[`degree_${idx}`] ? errors[`degree_${idx}`] : ""}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="date"
                label="Year of Joining"
                name="yearOfJoining"
                placeholder="e.g., 2018"
                value={edu.yearOfJoining}
                onChange={(name, value) => handleFieldChange(idx, name, value)}
                required
                error={touched[`yearOfJoining_${idx}`] && !!errors[`yearOfJoining_${idx}`]}
                errorText={touched[`yearOfJoining_${idx}`] ? errors[`yearOfJoining_${idx}`] : ""}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="date"
                label="Year of Completion"
                name="yearOfCompletion"
                placeholder="e.g., 2022"
                value={edu.yearOfCompletion}
                onChange={(name, value) => handleFieldChange(idx, name, value)}
                required
                error={touched[`yearOfCompletion_${idx}`] && !!errors[`yearOfCompletion_${idx}`]}
                errorText={touched[`yearOfCompletion_${idx}`] ? errors[`yearOfCompletion_${idx}`] : ""}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="text"
                label="CGPA / Percentage"
                name="cgpa"
                value={edu.cgpa}
                onChange={(name, value) => handleFieldChange(idx, name, value)}
                required
                error={touched[`cgpa_${idx}`] && !!errors[`cgpa_${idx}`]}
                errorText={touched[`cgpa_${idx}`] ? errors[`cgpa_${idx}`] : ""}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="text"
                label="College Name"
                name="collegeName"
                value={edu.collegeName}
                onChange={(name, value) => handleFieldChange(idx, name, value)}
                required
                error={touched[`collegeName_${idx}`] && !!errors[`collegeName_${idx}`]}
                errorText={touched[`collegeName_${idx}`] ? errors[`collegeName_${idx}`] : ""}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="text"
                label="Affiliated to University"
                name="university"
                value={edu.university}
                onChange={(name, value) => handleFieldChange(idx, name, value)}
                required
                error={touched[`university_${idx}`] && !!errors[`university_${idx}`]}
                errorText={touched[`university_${idx}`] ? errors[`university_${idx}`] : ""}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DynamicFieldAdvanced
                type="autocomplete"
                label="State"
                name="state"
                value={edu.state}
                onChange={(name, value) => handleFieldChange(idx, name, value)}
                options={stateList.map(s => ({ value: s, label: s }))}
                required
                error={touched[`state_${idx}`] && !!errors[`state_${idx}`]}
                errorText={touched[`state_${idx}`] ? errors[`state_${idx}`] : ""}
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
                Attachments{' '}
                <span style={{ color: '#EF4444' }}>*</span>
                {touched[`attachments_${idx}`] && errors[`attachments_${idx}`] && (
                  <Typography component="span" sx={{ color: '#EF4444', ml: 1, fontSize: '0.75rem', fontWeight: 600 }}>
                    ({errors[`attachments_${idx}`]})
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: touched[`attachments_${idx}`] && errors[`attachments_${idx}`] ? '#EF4444' : 'rgba(117, 34, 209, 0.3)',
                    color: touched[`attachments_${idx}`] && errors[`attachments_${idx}`] ? '#EF4444' : '#7522D1',
                    backgroundColor: touched[`attachments_${idx}`] && errors[`attachments_${idx}`] ? 'rgba(239, 68, 68, 0.02)' : 'rgba(117, 34, 209, 0.02)',
                    fontWeight: 700,
                    '&:hover': {
                      borderColor: touched[`attachments_${idx}`] && errors[`attachments_${idx}`] ? '#EF4444' : '#7522D1',
                      backgroundColor: touched[`attachments_${idx}`] && errors[`attachments_${idx}`] ? 'rgba(239, 68, 68, 0.06)' : 'rgba(117, 34, 209, 0.06)',
                    },
                  }}
                  onClick={() => fileInputRefs.current[idx] && fileInputRefs.current[idx].click()}
                >
                  Choose files
                </Button>
                <FileList
                  files={edu.attachments}
                  onRemove={(fileIdx) => handleRemoveAttachment(idx, fileIdx)}
                />
              </Box>
              <input
                ref={el => (fileInputRefs.current[idx] = el)}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={e => handleFileUpload(idx, e)}
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleSkip}
            size="large"
            disabled={isSubmitting}
          >
            Skip for now
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            size="large"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : (employeeId ? "Update" : "Save & Next")}
          </Button>
        </Box>
      </Box>
      <Notification />
    </Box>
  );
};

export default EducationalDetails;
